import React, { useEffect, useState } from "react";
import { EmptyState } from "../../../../components/recharts/ReMixCharts/components/EmptyState";
import { SheetTableSkeleton } from "./SheetTableSkeleton";

const MONTH_LABELS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const METRICS = ["ISGS", "GNA", "REMC", "SGS", "Total"] as const;
type MetricKey = "isgs" | "gna" | "remc" | "sgs" | "total";
const METRIC_KEYS: MetricKey[] = ["isgs", "gna", "remc", "sgs", "total"];

interface SummaryMonth {
  month: number;
  isgs: number;
  gna: number;
  remc: number;
  sgs: number;
  total: number;
}

interface SummaryRow {
  time_block: number;
  time_slot: string;
  months: SummaryMonth[];
}

interface Props {
  date: string;
  fetchFn: (p?: { date?: string }) => Promise<any>;
  onDataLoaded?: (data: any) => void;
}

const TEAL      = "#34656D";
const BORDER    = "1px solid #E2E8F0";
const HDR_BG    = "linear-gradient(#EDF4F5,#EDF4F5)";
const COL_W     = 88;   // metric column width
const TS_W      = 130;  // time-slot column width
const GROUP_SEP = `2px solid ${TEAL}`;  // separator between month groups

export const AvailabilitySummaryTable: React.FC<Props> = ({ date, fetchFn, onDataLoaded }) => {
  const [data, setData] = useState<{ data: SummaryRow[] } | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!date) return;
    setLoading(true);
    fetchFn({ date })
      .then((d) => { setData(d); onDataLoaded?.(d); })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [date, fetchFn]);

  if (loading) return <SheetTableSkeleton cols={10} />;

  if (!data || !data.data || data.data.length === 0) {
    return (
      <div className="flex-1 min-h-0 relative bg-white rounded-xl" style={{ border: BORDER, minHeight: 200 }}>
        <EmptyState />
      </div>
    );
  }

  const rows = data.data;
  const presentMonths = [...new Set(rows.flatMap((r) => r.months.map((m) => m.month)))].sort((a, b) => a - b);

  return (
    <div className="flex-1 min-h-0 rounded-xl overflow-hidden bg-white flex flex-col mt-2"
      style={{ border: BORDER, boxShadow: "0 2px 12px rgba(52,101,109,0.08)" }}>
      <div style={{ overflowX: "auto", overflowY: "auto", flex: 1, minHeight: 0 }}>
        <table style={{ borderCollapse: "separate", borderSpacing: 0, fontSize: 12, minWidth: "100%" }}>
          <thead style={{ position: "sticky", top: 0, zIndex: 10 }}>

            {/* ── Row 1: month group headers ── */}
            <tr>
              <th rowSpan={2} style={{
                position: "sticky", top: 0, left: 0, zIndex: 22,
                backgroundImage: HDR_BG, color: TEAL, fontWeight: 700, fontSize: 13,
                minWidth: TS_W, border: BORDER,
                borderRight: GROUP_SEP, borderBottom: GROUP_SEP,
                boxShadow: "3px 0 6px rgba(52,101,109,0.12)",
                textAlign: "center", verticalAlign: "middle", padding: "0 12px",
              }}>
                Time Slot
              </th>
              {presentMonths.map((m, mi) => (
                <th key={m} colSpan={METRICS.length} style={{
                  backgroundImage: HDR_BG, color: TEAL, fontWeight: 700, fontSize: 13,
                  border: BORDER,
                  borderLeft: mi > 0 ? GROUP_SEP : BORDER,
                  borderBottom: "1px solid #CAD5E2",
                  textAlign: "center", padding: "10px 0",
                  position: "sticky", top: 0, zIndex: 10,
                  letterSpacing: "0.02em",
                }}>
                  {MONTH_LABELS[m - 1]}
                </th>
              ))}
            </tr>

            {/* ── Row 2: metric sub-headers ── */}
            <tr>
              {presentMonths.map((m, mi) =>
                METRIC_KEYS.map((key, ki) => {
                  const isTotal   = ki === METRIC_KEYS.length - 1;
                  const isFirst   = ki === 0;
                  return (
                    <th key={`${m}-${key}`} style={{
                      backgroundImage: HDR_BG, color: TEAL,
                      fontWeight: isTotal ? 700 : 500, fontSize: 11,
                      border: BORDER,
                      borderLeft: (mi > 0 && isFirst) ? GROUP_SEP : BORDER,
                      borderBottom: "2px solid #CAD5E2",
                      textAlign: "center", minWidth: COL_W, padding: "6px 4px",
                      position: "sticky", top: 39, zIndex: 9,
                      whiteSpace: "nowrap",
                      backgroundColor: isTotal ? "#dff0f2" : undefined,
                    }}>
                      {METRICS[ki]}
                    </th>
                  );
                })
              )}
            </tr>
          </thead>

          <tbody>
            {rows.map((row, idx) => {
              const even     = idx % 2 === 0;
              const rowBg    = even ? "#FFFFFF" : "#F7FAFB";
              const monthMap = new Map(row.months.map((m) => [m.month, m]));
              return (
                <tr key={row.time_block} style={{ backgroundColor: rowBg }}>

                  {/* sticky Time Slot cell */}
                  <td style={{
                    position: "sticky", left: 0, zIndex: 5,
                    backgroundColor: rowBg, color: TEAL, fontWeight: 500, fontSize: 12,
                    height: 36, minWidth: TS_W, whiteSpace: "nowrap",
                    border: BORDER, borderRight: GROUP_SEP,
                    boxShadow: "3px 0 6px rgba(52,101,109,0.08)",
                    textAlign: "center", verticalAlign: "middle",
                  }}>
                    {row.time_slot}
                  </td>

                  {presentMonths.map((m, mi) => {
                    const md = monthMap.get(m);
                    return METRIC_KEYS.map((key, ki) => {
                      const isTotal = ki === METRIC_KEYS.length - 1;
                      const isFirst = ki === 0;
                      const val     = md ? md[key] : null;
                      return (
                        <td key={`${m}-${key}`} style={{
                          border: BORDER,
                          borderLeft: (mi > 0 && isFirst) ? GROUP_SEP : BORDER,
                          textAlign: "center", verticalAlign: "middle",
                          fontSize: 12, padding: "0 6px",
                          height: 36, minWidth: COL_W,
                          color: val === null ? "#D4D4D8" : "#374151",
                          fontWeight: isTotal ? 600 : 400,
                          backgroundColor: isTotal
                            ? even ? "#f2fafb" : "#eaf5f6"
                            : undefined,
                        }}>
                          {val === null ? "–" : val === 0 ? "0" : (val as number).toFixed(2)}
                        </td>
                      );
                    });
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
